/* eslint-disable max-len */
import { DownOutlined } from '@ant-design/icons';
import { Col, Collapse, Divider, Form, Modal, Row } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import TableComponent from 'components/atoms/TableComponent';
import Title from 'components/atoms/Title';
import TooltipComponent from 'components/atoms/TooltipComponent';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';
import { RowTitles } from 'components/molecules/RowTitles';
import ErrModal from 'components/molecules/SpecialniSluzby/ErrModal';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import fileDownload from 'services/fileDownload';
import uePozadavkyDetailApi, {
    ETypPrevodu,
    IData,
    IPozadavky,
    IPozadavkyPolozka
} from 'services/specialniSluzby/uePozadavky';
import uePrehledApi, { IPrehled, IPrehledPozadavek } from 'services/specialniSluzby/uePrehled';
import { ueSouborVystupCSV } from 'services/specialniSluzby/ueSoubor';
import { ueZruseni } from 'services/specialniSluzby/ueZruseni';

import routePathEnum from 'enums/routePathEnum';
import { EStavPozadavku } from 'enums/specialniSluzby/stavPozadavku';
import { ETypSeznam } from 'enums/specialniSluzby/typSeznam';

import identifikaceOsobySelect from 'data/codebooks/amlz/identifikaceOsobySelect';
import { typStavZadostiSelect } from 'data/codebooks/specialniSluzby/typStavZadosti';

import formatInputNumberThousand from 'utils/formatInputNumberThousand';
import getLanguageObject from 'utils/getLanguageObject';

import { rowTitleStylesCollapse } from './DetailPozadavkuDLT';
import DvpSeznam from './DvpSeznam';

const rowTitleStyles = {
    maxWidth: '650px',
    marginBottom: '6px',
    fontSize: '18px'
};

const DetailPozadavku: React.FC = () => {
    const { confirm } = Modal;
    const { Panel } = Collapse;
    const { aktivniFirma, specialniSluzby, specialniSluzbyDVP, language } = useContext(Context);
    const { setLevel } = useProfily();
    const { navigateWorkaround } = useNavigateWorkaround();
    const [form] = Form.useForm();
    const [form2] = Form.useForm();
    const [errStatus, setErrStatus] = useState<number | string>();
    const [loading, setLoading] = useState<boolean>(false);
    const [refetch, setRefetch] = useState<boolean>(false);
    const [prehledData, setPrehledData] = useState<IPrehledPozadavek | undefined>();
    const [data, setData] = useState<IPozadavky | undefined>();
    const [errModalMsg, setErrModalMsg] = useState<string>();
    const [dataFilterActive, setDataFilterActive] = useState<boolean>(false);
    const [selectedDetail, setSelectedDetail] = useState<IPozadavkyPolozka[]>();
    const [pagination, setPagination] = useState<{
        offset: number;
        page: number;
        limit: number;
        order: string;
    }>({
        offset: 0,
        page: 1,
        limit: 10,
        order: 'poradi:desc:idpolozky:desc'
    });
    const [isDvpDetail, setIsDvpDetail] = useState<boolean>(false);

    // nastavenie casovej zony pre tuto page
    useEffect(() => {
        dayjs.tz.setDefault('Europe/Prague');
        return () => {
            dayjs.tz.setDefault();
        };
    }, []);

    const titleDate =
        specialniSluzby?.detail?.meznidatum || prehledData?.meznidatum
            ? `${dayjs(specialniSluzby?.detail?.meznidatum || prehledData?.meznidatum).format(
                  'DD.MM.YYYY'
              )} ${specialniSluzby?.detail?.typprevodu !== ETypPrevodu.BEZZAPLACENI && !specialniSluzbyDVP?.detailDVP?.pocetcpvn ? '11:00' : '16:30'}`
            : '';

    const title = `${getLanguageObject(language)['SS_NOVY_POZADAVEK_NA_' + specialniSluzby?.detail?.typ]} - ${
        getLanguageObject(language).ISIN
    }: ${specialniSluzby?.detail?.isin}, ${getLanguageObject(language).DATUM}: ${dayjs(
        specialniSluzby?.detail?.datum
    ).format('DD.MM.YYYY')} ${
        specialniSluzby?.detail?.puvodcdcp ? '(' + getLanguageObject(language).ZADANO_CDCP + ')' : ''
    } ${getLanguageObject(language).LZE_EVIDOVAT_DO}: ${titleDate} ${
        specialniSluzby?.detail?.typprevodu
            ? ', ' + getLanguageObject(language)['TYP_PREVODU_' + specialniSluzby.detail.typprevodu]
            : ''
    }`;

    const isAfterMezniDatum = (isDVP: boolean) =>
        titleDate
            ? dayjs(
                  `${dayjs(specialniSluzby?.detail?.meznidatum || prehledData?.meznidatum).format(
                      'YYYY-MM-DD'
                  )} ${specialniSluzby?.detail?.typprevodu !== ETypPrevodu.BEZZAPLACENI && isDVP ? '11:00' : '16:30'}`
              ) < dayjs.tz()
            : false;

    const loadData = () => {
        if (specialniSluzby?.detail) {
            setLoading(true);
            const formItems = form.getFieldsValue();
            formItems.offset = pagination.offset !== undefined ? pagination.offset : 0;
            formItems.limit = pagination.limit;
            formItems.order = pagination.order;

            // načtu všechny požadavky (případně dle nastaveného filtru)
            // uePozadavkyDetailApi(specialniSluzby.detail.id, form.getFieldsValue())

            uePozadavkyDetailApi(
                specialniSluzby?.detail?.id!,
                Object.fromEntries(
                    Object.entries({
                        ...formItems
                    }).filter(([, v]) => v !== undefined && v !== '')
                ) as unknown as IData
            )
                .then((resp: IPozadavky) => {
                    setLevel('specialniSluzby', {
                        ...specialniSluzby,
                        detail: {
                            ...specialniSluzby?.detail,
                            ...resp
                        } as IPrehledPozadavek
                    });
                    setData(resp);
                    form2.resetFields();
                })
                .catch((err: any) => {
                    err.response?.status === 404 && setData(undefined);
                    setErrStatus(err.response?.status);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    useEffect(() => {
        if (data) {
            const formItems = form.getFieldsValue();
            formItems.offset = pagination.offset !== undefined ? pagination.offset : 0;
            formItems.limit = pagination.limit;
            formItems.order = pagination.order;
            setLevel('specialniSluzby', {
                ...specialniSluzby,
                detail: {
                    ...specialniSluzby?.detail,
                    ...data
                } as IPrehledPozadavek,
                detailFiltr: Object.fromEntries(
                    Object.entries({
                        ...formItems
                    }).filter(([, v]) => v !== undefined && v !== '')
                ) as unknown as IData,
                pozadavek: undefined
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const columns = [
        {
            title: <TranslateText>KOD_UCASTNIKA</TranslateText>,
            fixed: true,
            dataIndex: 'koducastnika',
            key: 'koducastnika',
            sorter: true
        },
        {
            title: <TranslateText>UCET</TranslateText>,
            dataIndex: 'ucet',
            key: 'ucet',
            fixed: true,
            sorter: true
        },
        {
            title: <TranslateText>ID_NABYVATELE</TranslateText>,
            dataIndex: 'idnabyvatele',
            key: 'idnabyvatele',
            sorter: true
        },
        {
            title: <TranslateText>POCET_CP</TranslateText>,
            dataIndex: 'pocetcp',
            key: 'pocetcp',
            sorter: true,
            render: (val: string) => formatInputNumberThousand(val || 0)
        },
        {
            title: <TranslateText>CISLO_ZASTAVNI_SMLOUVY_ROW</TranslateText>,
            dataIndex: 'cislozastavnismlouvy',
            key: 'cislozastavnismlouvy',
            sorter: true
        },
        {
            title: <TranslateText>ID_MAJITELE</TranslateText>,
            dataIndex: 'idvlastnika',
            key: 'idvlastnika',
            sorter: true
        },
        {
            title: <TranslateText>JMENO</TranslateText>,
            dataIndex: 'jmeno',
            key: 'jmeno',
            sorter: true
        },
        {
            title: <TranslateText>PRIJMENI</TranslateText>,
            dataIndex: 'prijmeni',
            key: 'prijmeni',
            sorter: true
        },
        /* {
            title: <TranslateText>ID_PREVODU_V_CDCP</TranslateText>,
            dataIndex: 'idvcdcp',
            key: 'idvcdcp'
            sorter: true,
        }, */
        {
            title: <TranslateText>STAV</TranslateText>,
            dataIndex: 'stav',
            key: 'stav',
            sorter: true,
            render: (text: string) => <TranslateText>{'SS_DETAIL_STAV_' + text}</TranslateText>
        },
        {
            ellipsis: {
                showTitle: false
            },
            key: 'sankce',
            sorter: true,
            width: 24,
            dataIndex: 'sankce',
            render: (sankce: boolean) =>
                sankce ? (
                    <TooltipComponent title="SS_SANKCE_TOOLTIP">
                        <p
                            style={{
                                color: 'red',
                                fontWeight: 'bold',
                                fontSize: 20,
                                lineHeight: 0,
                                position: 'relative',
                                top: 9
                            }}
                        >
                            !
                        </p>
                    </TooltipComponent>
                ) : (
                    <></>
                )
        },
        {
            title: <TranslateText>PODROBNOSTI</TranslateText>,
            dataIndex: 'podrobnosti',
            key: 'podrobnosti',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            fixed: 'right' as any,
            render: (_text: string, row: IPozadavkyPolozka, index: number) => (
                <AnchorComponent
                    id={`zobrazitBtn${index}`}
                    onClick={() => {
                        setSelectedDetail([row]);
                        setLevel('specialniSluzby', {
                            ...specialniSluzby,
                            pozadavek: {
                                ...row
                            }
                        });
                    }}
                >
                    <TranslateText>ZOBRAZIT_DETAIL</TranslateText>
                </AnchorComponent>
            )
        }
    ];
    const columnsSICAV = [
        {
            title: <TranslateText>KOD_UCASTNIKA</TranslateText>,
            fixed: true,
            dataIndex: 'koducastnika',
            sorter: true,

            key: 'koducastnika'
        },
        {
            title: <TranslateText>UCET</TranslateText>,
            dataIndex: 'ucet',
            sorter: true,
            fixed: true,
            key: 'ucet'
        },
        {
            title: <TranslateText>ID_NABYVATELE</TranslateText>,
            dataIndex: 'idnabyvatele',
            sorter: true,
            key: 'idnabyvatele'
        },
        {
            title: <TranslateText>POCET_CP</TranslateText>,
            dataIndex: 'pocetcp',
            sorter: true,
            key: 'pocetcp',
            width: 110,
            render: (val: string) => formatInputNumberThousand(val || 0)
        },
        // Odstraněno na požádání (spolu s dalšími sloupci, kvůli přehlednosti)
        /* {
            title: <TranslateText>KOD_UCASTNIKA</TranslateText>,
            dataIndex: 'koducastnika',
            sorter: true,
            key: 'koducastnika'
        }, */
        {
            title: <TranslateText>ID_MAJITELE</TranslateText>,
            dataIndex: 'idvlastnika',
            sorter: true,

            key: 'idvlastnika'
        },
        {
            title: <TranslateText>JMENO</TranslateText>,
            dataIndex: 'jmeno',
            sorter: true,

            key: 'jmeno'
        },
        {
            title: <TranslateText>PRIJMENI</TranslateText>,
            dataIndex: 'prijmeni',
            sorter: true,

            key: 'prijmeni'
        },
        /* {
            title: <TranslateText>ID_PREVODU_V_CDCP</TranslateText>,
            dataIndex: 'idvcdcp',
            sorter: true,
            key: 'idvcdcp'
        },*/
        {
            title: <TranslateText>STAV</TranslateText>,
            dataIndex: 'stav',
            sorter: true,
            key: 'stav',
            render: (text: string) => <TranslateText>{'SS_DETAIL_STAV_' + text}</TranslateText>
        },
        {
            ellipsis: {
                showTitle: false
            },
            key: 'sankce',
            width: 24,
            dataIndex: 'sankce',
            sorter: true,
            render: (sankce: boolean) =>
                sankce ? (
                    <TooltipComponent title="SS_SANKCE_TOOLTIP">
                        <p
                            style={{
                                color: 'red',
                                fontWeight: 'bold',
                                fontSize: 20,
                                lineHeight: 0,
                                position: 'relative',
                                top: 9
                            }}
                        >
                            !
                        </p>
                    </TooltipComponent>
                ) : (
                    <></>
                )
        },
        {
            title: <TranslateText>PODROBNOSTI</TranslateText>,
            dataIndex: 'podrobnosti',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            fixed: 'right' as any,
            key: 'podrobnosti',
            render: (_text: string, row: IPozadavkyPolozka, index: number) => (
                <AnchorComponent
                    id={`zobrazitBtnSicav${index}`}
                    onClick={() => {
                        setSelectedDetail([row]);
                        setLevel('specialniSluzby', {
                            ...specialniSluzby,
                            pozadavek: {
                                ...row
                            }
                        });
                    }}
                >
                    <TranslateText>ZOBRAZIT_DETAIL</TranslateText>
                </AnchorComponent>
            )
        }
    ];

    const rowsSpan: number = 12;
    const columnsDetail = (
        <>
            {specialniSluzby?.detail?.isin && (
                <RowTitles
                    firstText="ISIN"
                    secondText={String(specialniSluzby?.detail.isin)}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={rowsSpan}
                />
            )}
            {specialniSluzby?.detail?.datum && (
                <RowTitles
                    firstText="DATUM"
                    secondText={String(specialniSluzby ? dayjs(specialniSluzby.detail.datum).format('DD.MM.YYYY') : '')}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={rowsSpan}
                />
            )}
            {selectedDetail && selectedDetail[0].idpolozky && (
                <RowTitles
                    firstText="ISB_ID"
                    secondText={String(selectedDetail && selectedDetail[0].idpolozky)}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={rowsSpan}
                />
            )}
            {selectedDetail && selectedDetail[0].koducastnika && (
                <RowTitles
                    firstText="KOD_UCASTNIKA"
                    secondText={String(selectedDetail && selectedDetail[0].koducastnika)}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={rowsSpan}
                />
            )}
            {selectedDetail && selectedDetail[0].ucet && (
                <RowTitles
                    firstText="UCET"
                    secondText={String(selectedDetail && selectedDetail[0].ucet)}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={rowsSpan}
                />
            )}
            {selectedDetail && selectedDetail[0].idnabyvatele && (
                <RowTitles
                    firstText="ID_NABYVATELE"
                    secondText={String(selectedDetail && selectedDetail[0].idnabyvatele)}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={rowsSpan}
                />
            )}
            {selectedDetail && selectedDetail[0].idvcdcp && (
                <RowTitles
                    firstText="IDVCDCP"
                    secondText={selectedDetail[0].idvcdcp}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={rowsSpan}
                />
            )}
            {selectedDetail && selectedDetail[0].pocetcp && (
                <RowTitles
                    firstText={specialniSluzby?.detail?.typ === 'SICAV' ? 'POCET_CP_PRODEJ' : 'POCET_CP'}
                    secondText={formatInputNumberThousand(String(selectedDetail && selectedDetail[0].pocetcp))}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={rowsSpan}
                />
            )}

            {selectedDetail && selectedDetail[0].poznamka && (
                <RowTitles
                    firstText="POZNAMKA"
                    secondText={String(selectedDetail && selectedDetail[0].poznamka)}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={rowsSpan}
                    className="rowtiles-second"
                />
            )}
            {selectedDetail && selectedDetail[0].cislozastavnismlouvy && (
                <RowTitles
                    firstText="CISLO_ZASTAVNI_SMLOUVY"
                    secondText={String(selectedDetail && selectedDetail[0].cislozastavnismlouvy)}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={rowsSpan}
                />
            )}
            {selectedDetail && selectedDetail[0].ppn && (
                <RowTitles firstText="PPN_SELECT" secondText={'ANO'} paragraph={true} style={rowTitleStyles} />
            )}
            {selectedDetail && selectedDetail[0].stav && (
                <RowTitles
                    firstText="STAV"
                    secondText={String(selectedDetail && 'SS_DETAIL_STAV_' + selectedDetail[0].stav)}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={rowsSpan}
                />
            )}
            {selectedDetail && selectedDetail[0].zpusobporizeni && (
                <RowTitles
                    firstText="ZPUSOB_PORIZENI"
                    secondText={String(
                        selectedDetail && selectedDetail[0].zpusobporizeni
                            ? 'SS_DETAIL_TAB_PORIZENI_' + selectedDetail[0].zpusobporizeni
                            : ''
                    )}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={rowsSpan}
                />
            )}
            {selectedDetail && selectedDetail[0].uzivatel && (
                <RowTitles
                    firstText="UZIVATEL"
                    secondText={String(selectedDetail && selectedDetail[0].uzivatel)}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={rowsSpan}
                />
            )}
            {selectedDetail && selectedDetail[0].upozorneni && (
                <RowTitles
                    firstText="UPOZORNENI"
                    secondText={String(selectedDetail && selectedDetail[0].upozorneni)}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={rowsSpan}
                />
            )}
            {selectedDetail && selectedDetail[0].meznidatumprozruseni && (
                <RowTitles
                    firstText="SS_DETAIL_VYBRANY_MEZNI_DATUM_ZRUSENI"
                    secondText={String(
                        selectedDetail && dayjs(selectedDetail[0].meznidatumprozruseni).format('DD.MM.YYYY')
                    )}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={rowsSpan}
                />
            )}
            {selectedDetail && selectedDetail[0].lzeevidovatod && (
                <RowTitles
                    firstText="SS_DETAIL_VYBRANY_LZE_ZAEVIDOVAT_OD"
                    secondText={String(selectedDetail && dayjs(selectedDetail[0].lzeevidovatod).format('DD.MM.YYYY'))}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={rowsSpan}
                />
            )}
        </>
    );

    useEffect(() => {
        if (specialniSluzby?.detail) {
            if (
                (!specialniSluzby?.detail?.meznidatum || !specialniSluzby.detail.typprevodu) &&
                specialniSluzby?.detail?.id
            ) {
                uePrehledApi({
                    aktivni: true
                }).then((resp: IPrehled) => {
                    const selectedZadost = resp.pozadavky.filter(e => e.id === specialniSluzby.detail?.id)[0];
                    setPrehledData(selectedZadost);
                    setLevel('specialniSluzby', {
                        ...specialniSluzby,
                        detail: {
                            ...specialniSluzby?.detail,
                            ...selectedZadost
                        } as IPrehledPozadavek,

                        pozadavek: undefined
                    });
                    loadData();
                });
            } else if (specialniSluzby.detail.id) loadData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [specialniSluzby?.detail?.id, pagination, refetch]);

    const filterData = () => {
        setDataFilterActive(true);
        loadData();
    };

    const multipleUseBtns = {
        zpet: (
            <ButtonComponent
                id="zpetBtn"
                buttontext="ZPET"
                type="default"
                onClick={() => {
                    navigateWorkaround(-1);
                }}
            />
        ),
        zrusit: (
            <ButtonComponent
                buttontext="SMAZAT_POZADAVEK"
                id="smazatBtn"
                onClick={() => {
                    confirm({
                        title: <TranslateText>POTVRDENI</TranslateText>,
                        content: `${getLanguageObject(language).DELETE_CONFIRM_TEXT} ${
                            getLanguageObject(language)['SS_NOVY_POZADAVEK_NA_' + specialniSluzby?.detail?.typ]
                        } - ${getLanguageObject(language).ISIN}: ${specialniSluzby?.detail?.isin}, ${
                            getLanguageObject(language).DATUM
                        }: ${dayjs(specialniSluzby?.detail?.datum).format('DD.MM.YYYY')}`,
                        cancelText: <TranslateText>NE</TranslateText>,
                        okText: <TranslateText>ANO</TranslateText>,
                        onOk() {
                            setLoading(true);
                            ueZruseni(specialniSluzby!.detail?.id!)
                                .then(resp => {
                                    if (resp.stav?.includes('TIMEOUT')) {
                                        setErrModalMsg('SS_SLUZBA_ERROR_NESKORO');
                                    } else {
                                        setLoading(false);
                                        navigateWorkaround(
                                            // eslint-disable-next-line max-len
                                            `/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_PREHLED_AKTIVNICH_POZADAVKU}`
                                        );
                                    }
                                })
                                .finally(() => setLoading(false));
                        }
                    });
                }}
            />
        )
    };

    const renderButtons = () => {
        return (
            <Row className="button-row">
                {(prehledData?.typprevodu || specialniSluzby?.detail?.typprevodu) !== ETypPrevodu.PROTIABEZZAPL &&
                    (prehledData?.typprevodu || specialniSluzby?.detail?.typprevodu) !== ETypPrevodu.PXVNABEZZAPL &&
                    multipleUseBtns.zpet}
                {/* - po stisknutí tlačítka se zobrazí stránka Ruční vložení požadavku
                    - jen v případě, že lze zadávat další položky - aktuální datum je více než 3 dny před datem požadavku. 
                */}
                {
                    // dayjs().diff(specialniSluzby?.detail.datum, 'd') < -2 &&
                    !isAfterMezniDatum(false) &&
                        specialniSluzby?.detail?.stav === EStavPozadavku.OTEVRENO &&
                        !specialniSluzby?.detail.puvodcdcp && (
                            <ButtonComponent
                                id="pridatPozadavekBtn"
                                buttontext="SS_PRIDAT_POZADAVEK"
                                onClick={() => {
                                    setLevel('specialniSluzby', {
                                        ...specialniSluzby,
                                        updateDetailu: false
                                    });

                                    navigateWorkaround(
                                        `/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_VLOZENI_RUCNI}`
                                    );
                                }}
                            />
                        )
                }
                {/* - po stisknutí tlačítka se zobrazí stránka Vložení požadavku souborem
                -    jen v případě, že lze zadávat další položky - tzn. aktuální datum je více než 3 dny před datem požadavku. 
                */}
                {
                    // dayjs().diff(specialniSluzby?.detail.datum, 'd') < -2 &&
                    !isAfterMezniDatum(false) &&
                        specialniSluzby?.detail?.stav === EStavPozadavku.OTEVRENO &&
                        !specialniSluzby?.detail.puvodcdcp && (
                            <ButtonComponent
                                id="pridatPozadavkeSouborBtn"
                                buttontext="SS_PRIDAT_ZE_SOUBORU"
                                onClick={() => {
                                    navigateWorkaround(
                                        `/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_VLOZENI_SOUBOR}`
                                    );
                                }}
                            />
                        )
                }

                {/* - po stisknutí tlačítka se zobrazí stránka Seznam požadavků pro zaevidování
                    - tlačítko se zobrazí jen v případě, že v tabulce je alespoň jedna položka, která není zaevidovaná v CDCP
                */}
                {/* {hasStavMensi(704) && ( */}
                {data &&
                    !isAfterMezniDatum(false) &&
                    (data?.zaevidovaneucty || 0) < (data?.pocetuctu || 0) &&
                    specialniSluzby?.detail?.stav === EStavPozadavku.OTEVRENO &&
                    !specialniSluzby?.detail.puvodcdcp && (
                        <ButtonComponent
                            id="zaevidovatBtn"
                            buttontext="SS_ZAEVIDOVAT_V_CDCP"
                            onClick={() => {
                                setLevel('specialniSluzby', {
                                    ...specialniSluzby,
                                    detail: {
                                        ...specialniSluzby.detail,
                                        polozky: (specialniSluzby.detail?.polozky || []).filter(
                                            e =>
                                                e.stav !== 'U006' &&
                                                (e.stav === 'U008'
                                                    ? e.lzeevidovatod
                                                        ? dayjs(e.lzeevidovatod) > dayjs()
                                                        : false
                                                    : e.stav === 'U007')
                                        )
                                    } as IPrehledPozadavek,
                                    seznamTyp: ETypSeznam.ZAEVIDOVANY
                                });
                                navigateWorkaround(`/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_SEZNAM}`);
                            }}
                        />
                    )}
                {/* - po stisknutí tlačítka se spustí generování souboru (zobrazí se informace o stažení). 
                    Zavolá se metoda GET /UE/{id}/soubor/vystupcsv. Aktuální filtr na stránce bude posílaný v parametrech metody.
                */}
                {data && data.count > 0 && !specialniSluzby?.detail?.puvodcdcp && (
                    <ButtonComponent
                        id="csvDownloadbtn"
                        buttontext="VYSTUP_CSV"
                        onClick={() => {
                            if (specialniSluzby) {
                                setLoading(true);
                                ueSouborVystupCSV(
                                    specialniSluzby?.detail?.id!,
                                    dataFilterActive ? form.getFieldsValue() : null
                                )
                                    .then(file => {
                                        if (file.redirect) {
                                            fileDownload(file.redirect).finally(() => setLoading(false));
                                        }
                                    })
                                    .catch((err: any) => {
                                        console.log('MHO GET CSV error', err);
                                    })
                                    .finally(() => {
                                        setLoading(false);
                                    });
                            }
                        }}
                    />
                )}
                {/* - po stisknutí tlačítka se zobrazí stránka Seznam požadavků pro smazání
                    - tlačítko se zobrazí jen v případě, že pocetcpuspesnezaevidovano je menší než pocetcp
                */}
                {
                    // (specialniSluzby?.detail.typ === 'SICAV'
                    //     ? data?.pocetcp + data?.pocetcpodkup
                    //     : data?.pocetcp) &&
                }
                {!!data?.count &&
                    !isAfterMezniDatum(false) &&
                    specialniSluzby?.detail?.stav === EStavPozadavku.OTEVRENO &&
                    !specialniSluzby?.detail.puvodcdcp && (
                        <ButtonComponent
                            id="smazatBtn"
                            buttontext="SMAZAT"
                            onClick={() => {
                                setLevel('specialniSluzby', {
                                    ...specialniSluzby,
                                    seznamTyp: ETypSeznam.SMAZANY
                                });
                                navigateWorkaround(`/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_SEZNAM}`);
                            }}
                        />
                    )}
                {(prehledData?.typprevodu || specialniSluzby?.detail?.typprevodu) !== ETypPrevodu.PROTIABEZZAPL &&
                    (prehledData?.typprevodu || specialniSluzby?.detail?.typprevodu) !== ETypPrevodu.PXVNABEZZAPL &&
                    !data?.count &&
                    !specialniSluzby?.detail?.puvodcdcp &&
                    multipleUseBtns.zrusit}
            </Row>
        );
    };

    const renderProtiabezzapExtraBtns = () => (
        <Row>
            {multipleUseBtns.zpet}
            {!data?.count &&
                !specialniSluzby?.detail?.puvodcdcp &&
                !specialniSluzbyDVP?.detailDVP?.count &&
                multipleUseBtns.zrusit}
        </Row>
    );

    const renderSelectedDetail = () => {
        return (
            <Modal
                open={!!selectedDetail}
                onCancel={() => setSelectedDetail(undefined)}
                title={
                    <>
                        <TranslateText>SS_DETAIL_VYBRANY</TranslateText>{' '}
                        <TranslateText>{'SS_NOVY_POZADAVEK_NA_' + specialniSluzby?.detail?.typ}</TranslateText>
                    </>
                }
                footer={[
                    <Row className="button-row-modal">
                        {selectedDetail &&
                            !specialniSluzby?.detail?.puvodcdcp &&
                            ['U001', 'U002', 'U003', 'U006', 'U007', 'U008', 'U700', 'U701', 'U730'].includes(
                                selectedDetail[0].stav || ''
                            ) && (
                                <ButtonComponent
                                    id="smazatVybranyBtn"
                                    buttontext="SS_DETAIL_VYBRANY_SMAZAT"
                                    disabled={selectedDetail[0].sankce}
                                    onClick={() => {
                                        setLevel('specialniSluzby', {
                                            ...specialniSluzby,
                                            seznamTyp: ETypSeznam.SMAZANY
                                        });
                                        navigateWorkaround(
                                            `/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_SEZNAM}`
                                        );
                                    }}
                                />
                            )}
                        {selectedDetail &&
                            !specialniSluzby?.detail?.puvodcdcp &&
                            ['U001', 'U002', 'U003', 'U006', 'U007', 'U008', 'U730'].includes(
                                selectedDetail[0].stav || ''
                            ) && (
                                <ButtonComponent
                                    id="zmenitVybranyBtn"
                                    buttontext="SS_DETAIL_VYBRANY_ZMENIT"
                                    disabled={selectedDetail[0].sankce}
                                    onClick={() => {
                                        setLevel('specialniSluzby', {
                                            ...specialniSluzby,
                                            pozadavek: {
                                                ...selectedDetail[0]
                                            },
                                            updateDetailu: true
                                        });

                                        navigateWorkaround(
                                            `/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_VLOZENI_RUCNI}`
                                        );
                                    }}
                                />
                            )}
                    </Row>
                ]}
            >
                {columnsDetail}
            </Modal>
        );
    };

    const renderTable = () => (
        <>
            <FormWrapper onFinish={filterData} outForm={form}>
                <Row
                    gutter={8}
                    align="bottom"
                    style={{
                        marginTop: 10
                    }}
                >
                    <Col flex="1 1 120px">
                        <InputComponent name="koducastnika" label="KOD_UCASTNIKA" maxLength={3} notRequired={true} />
                    </Col>

                    <Col flex="1 1 150px">
                        <InputComponent name="ucet" label="UCET" notRequired={true} maxLength={12} />
                    </Col>

                    <Col flex="1 1 150px">
                        <InputComponent name="idnabyvatele" label="ID_NABYVATELE" maxLength={10} notRequired={true} />
                    </Col>

                    <Col flex="1 1 160px">
                        <InputComponent name="idvlastnika" label="ID_MAJITELE" maxLength={10} notRequired={true} />
                    </Col>

                    <Col flex="1 1 115px">
                        <InputComponent name="jmeno" label="JMENO" notRequired={true} />
                    </Col>

                    <Col flex="1 1 115px">
                        <InputComponent name="prijmeni" label="PRIJMENI" notRequired={true} />
                    </Col>

                    <Col flex="1 1 165px">
                        <SelectComponent
                            isFormItem={true}
                            data={typStavZadostiSelect}
                            name="stav"
                            labelParagraph="STAV_ZADOSTI"
                            notRequired={true}
                            showTooltipValue={true}
                        />
                    </Col>

                    {specialniSluzby?.detail?.sankce && (
                        <Col flex="1 1 145px">
                            <SelectComponent
                                labelParagraph="SANKCE"
                                allowClear={true}
                                isFormItem={true}
                                showTooltipValue={true}
                                notRequired={true}
                                name="sankce"
                                data={identifikaceOsobySelect}
                            />
                        </Col>
                    )}
                </Row>

                <Row
                    className="button-row-modal"
                    gutter={12}
                    style={{
                        position: 'relative',
                        top: -12
                    }}
                >
                    <ButtonComponent id="filtrSubmitBtn" buttontext="FILTROVAT" onClick={() => form.submit()} />
                    <ButtonComponent
                        id="filterClearBtn"
                        buttontext="VSECHNY"
                        onClick={() => {
                            form.resetFields();
                            setDataFilterActive(false);
                            loadData();
                        }}
                    />
                </Row>
            </FormWrapper>
            <TableComponent
                rowKey={(_, index) => index || 0}
                columns={specialniSluzby?.detail?.typ === 'SICAV' ? columnsSICAV : columns || []}
                scroll={{
                    x: true
                }}
                dataSource={data?.polozky}
                locale={{
                    emptyText: <TranslateText>TABULKA_ZADNE_ZAZNAMY</TranslateText>
                }}
                pagination={{
                    total: data?.count || 0,
                    onChange: page => {
                        setPagination({
                            page: page,
                            offset: (page - 1) * pagination.limit,
                            limit: pagination.limit,
                            order: pagination.order
                        });
                    },
                    // current: (data?.offset || 0) / 10 + 1,
                    showSizeChanger: false,
                    defaultPageSize: 10
                }}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={(paginat, _b, sorter: any) =>
                    setPagination(pv => {
                        return {
                            ...pv,
                            ...(sorter?.order && {
                                order: `${sorter.columnKey}:${
                                    sorter?.order === 'descend' ? 'desc' : 'asc'
                                }:idpolozky:desc`
                            }),
                            limit: paginat.pageSize || 10
                        };
                    })
                }
            />
            <FormWrapper onFinish={() => {}} outForm={form2}>
                <Row gutter={8}>
                    {data?.pocetcp ? (
                        <Col>
                            <InputComponent
                                name="pocetcp"
                                label="POCET_CP"
                                notRequired={true}
                                defaultValue={formatInputNumberThousand(data.pocetcp || 0)}
                                disabled={true}
                            />
                        </Col>
                    ) : null}
                    {data?.zaevidovanecp ? (
                        <Col>
                            <InputComponent
                                name="zaevidovancp"
                                label="ZAEVIDOVANE_CP"
                                notRequired={true}
                                defaultValue={formatInputNumberThousand(data.zaevidovanecp || 0)}
                                disabled={true}
                            />
                        </Col>
                    ) : null}
                    {data?.vyporadanecp ? (
                        <Col>
                            <InputComponent
                                name="vyporadanecp"
                                label="POCET_CP_VYPORADANYCH"
                                notRequired={true}
                                defaultValue={formatInputNumberThousand(data.vyporadanecp || 0)}
                                disabled={true}
                            />
                        </Col>
                    ) : null}
                    {data?.nevyporadanecp ? (
                        <Col>
                            <InputComponent
                                name="nevyporadanecp"
                                label="POCET_CP_NEVYPORADANYCH"
                                notRequired={true}
                                defaultValue={formatInputNumberThousand(data.nevyporadanecp || 0)}
                                disabled={true}
                            />
                        </Col>
                    ) : null}
                    {data?.pocetuctu ? (
                        <Col>
                            <InputComponent
                                name="pocetuctu"
                                label="SS_PREHLED_POCET_UCTU"
                                notRequired={true}
                                defaultValue={formatInputNumberThousand(data.pocetuctu || 0)}
                                disabled={true}
                            />
                        </Col>
                    ) : null}
                    {/* data?.pocetuctu &&
                    specialniSluzby?.detailDVP?.celkpocupis &&
                    (specialniSluzby?.detail?.typprevodu || prehledData?.typprevodu) === ETypPrevodu.PROTIABEZZAPL ? (
                        <Col>
                            <InputComponent
                                name="celkpocupis"
                                label="DOKUMENT_DODATEK_DVP_POCET_UPISOVATELU"
                                notRequired={true}
                                defaultValue={`${formatInputNumberThousand(data?.pocetuctu)} / ${formatInputNumberThousand(specialniSluzby?.detailDVP?.celkpocupis || 0)}`}
                                disabled={true}
                            />
                        </Col>
                    ) : null */}
                    {data?.kontrolauctu ? (
                        <Col>
                            <InputComponent
                                name="kontrolauctu"
                                label="KONTROLA_UCTU"
                                notRequired={true}
                                defaultValue={formatInputNumberThousand(data.kontrolauctu || 0)}
                                disabled={true}
                            />
                        </Col>
                    ) : null}
                    {data?.overeneucty ? (
                        <Col>
                            <InputComponent
                                name="overeneucty"
                                label="POCET_UCTU_OVERENE_2"
                                notRequired={true}
                                defaultValue={formatInputNumberThousand(data.overeneucty || 0)}
                                disabled={true}
                            />
                        </Col>
                    ) : null}
                    {data?.zaevidovaneucty ? (
                        <Col>
                            <InputComponent
                                name="zaevidovaneucty"
                                label="SS_DETAIL_USPESNE_POZADAVKY"
                                notRequired={true}
                                defaultValue={formatInputNumberThousand(data.zaevidovaneucty || 0)}
                                disabled={true}
                            />
                        </Col>
                    ) : null}
                </Row>
            </FormWrapper>{' '}
        </>
    );

    return (
        <LoadingWrapper loading={loading}>
            {(prehledData?.typprevodu || specialniSluzby?.detail?.typprevodu) !== ETypPrevodu.PROTIABEZZAPL ? (
                <Collapse expandIconPosition="right" bordered={false} className="titleCollapse">
                    <Panel header={<Title level={4} text={title} />} key="detail">
                        <div
                            style={{
                                width: 790
                            }}
                        >
                            {/* specialniSluzby?.detail?.meznidatum && (
                                <RowTitles
                                    firstText="LZE_ODESLAT"
                                    secondText={String(
                                        specialniSluzby?.detail?.meznidatum
                                            ? dayjs(specialniSluzby?.detail.meznidatum).format('DD.MM.YYYY') +
                                                  (specialniSluzby?.detail?.typprevodu === ETypPrevodu.PROTIZAPLACENI ||
                                                  specialniSluzby?.detail?.typprevodu === ETypPrevodu.PROTIABEZZAPL
                                                      ? ' 11:00'
                                                      : ' 16:30')
                                            : ''
                                    )}
                                    paragraph={true}
                                    style={rowTitleStylesCollapse}
                                    span={14}
                                />
                            )*/}
                            {specialniSluzby?.detail?.datuhr && (
                                <RowTitles
                                    firstText="DATUN_UHRADY_LATEST"
                                    secondText={String(
                                        specialniSluzby?.detail?.datuhr
                                            ? dayjs(specialniSluzby?.detail.datuhr).format('DD.MM.YYYY HH:mm')
                                            : ''
                                    )}
                                    paragraph={true}
                                    style={rowTitleStylesCollapse}
                                    span={14}
                                />
                            )}
                            {specialniSluzby?.detail?.cenacp && (
                                <RowTitles
                                    firstText="CENA_CP"
                                    secondText={formatInputNumberThousand(
                                        String(specialniSluzby?.detail ? specialniSluzby?.detail.cenacp : 0)
                                    )}
                                    paragraph={true}
                                    style={rowTitleStylesCollapse}
                                    span={14}
                                />
                            )}
                            {specialniSluzbyDVP?.detailDVP?.mena && (
                                <RowTitles
                                    firstText="MENA"
                                    secondText={String(
                                        specialniSluzbyDVP?.detailDVP ? specialniSluzbyDVP?.detailDVP?.mena : ''
                                    )}
                                    paragraph={true}
                                    style={rowTitleStylesCollapse}
                                    span={14}
                                />
                            )}
                            {specialniSluzbyDVP?.detailDVP?.bankucet && (
                                <RowTitles
                                    firstText="VYPLATA_BANKUC"
                                    secondText={String(
                                        specialniSluzbyDVP?.detailDVP ? specialniSluzbyDVP?.detailDVP.bankucet : ''
                                    )}
                                    paragraph={true}
                                    style={rowTitleStylesCollapse}
                                    span={14}
                                />
                            )}
                            {specialniSluzbyDVP?.detailDVP?.iban && (
                                <RowTitles
                                    firstText="IBAN"
                                    secondText={String(
                                        specialniSluzbyDVP?.detailDVP ? specialniSluzbyDVP?.detailDVP.iban : ''
                                    )}
                                    paragraph={true}
                                    style={rowTitleStylesCollapse}
                                    span={14}
                                />
                            )}
                            {!specialniSluzbyDVP?.detailDVP?.iban && !specialniSluzbyDVP?.detailDVP?.bankucet && (
                                <RowTitles
                                    firstText="VYPLATA_BANKUC"
                                    secondText={String(getLanguageObject(language).DVP_BANKUCET_NULL)}
                                    paragraph={true}
                                    style={rowTitleStylesCollapse}
                                    span={14}
                                />
                            )}
                            {specialniSluzbyDVP?.detailDVP?.bic && (
                                <RowTitles
                                    firstText="BIC_BANKY"
                                    secondText={String(
                                        specialniSluzbyDVP?.detailDVP ? specialniSluzbyDVP?.detailDVP.bic : ''
                                    )}
                                    paragraph={true}
                                    style={rowTitleStylesCollapse}
                                    span={14}
                                />
                            )}
                            {specialniSluzbyDVP?.detailDVP?.nazprij && (
                                <RowTitles
                                    firstText="NAZEV_PRIJEMCE"
                                    secondText={String(
                                        specialniSluzbyDVP?.detailDVP ? specialniSluzbyDVP?.detailDVP.nazprij : ''
                                    )}
                                    paragraph={true}
                                    style={rowTitleStylesCollapse}
                                    span={14}
                                />
                            )}
                            {specialniSluzbyDVP?.detailDVP?.adrprij1 && (
                                <RowTitles
                                    firstText="ADRESA_PRIJEMCE"
                                    secondText={String(
                                        specialniSluzbyDVP?.detailDVP ? specialniSluzbyDVP?.detailDVP.adrprij1 : ''
                                    )}
                                    paragraph={true}
                                    style={rowTitleStylesCollapse}
                                    span={14}
                                />
                            )}
                            {specialniSluzbyDVP?.detailDVP?.adrprij2 && (
                                <RowTitles
                                    firstText="ADRESA_PRIJEMCE"
                                    secondText={String(
                                        specialniSluzbyDVP?.detailDVP ? specialniSluzbyDVP?.detailDVP.adrprij2 : ''
                                    )}
                                    paragraph={true}
                                    style={rowTitleStylesCollapse}
                                    span={14}
                                />
                            )}
                            {specialniSluzbyDVP?.detailDVP?.adrprij3 && (
                                <RowTitles
                                    firstText="ADRESA_PRIJEMCE"
                                    secondText={String(
                                        specialniSluzbyDVP?.detailDVP ? specialniSluzbyDVP?.detailDVP.adrprij3 : ''
                                    )}
                                    paragraph={true}
                                    style={rowTitleStylesCollapse}
                                    span={14}
                                />
                            )}
                            {specialniSluzbyDVP?.detailDVP?.bicprij && (
                                <RowTitles
                                    firstText="PRIJEMCE_BIC"
                                    secondText={String(
                                        specialniSluzbyDVP?.detailDVP ? specialniSluzbyDVP?.detailDVP.bicprij : ''
                                    )}
                                    paragraph={true}
                                    style={rowTitleStylesCollapse}
                                    span={14}
                                />
                            )}
                        </div>
                    </Panel>
                </Collapse>
            ) : (
                <Title level={4} text={title} />
            )}
            {errStatus && errStatus !== 404 ? (
                <Row>
                    <Col span={16}>
                        {errStatus === 404 ? (
                            <Paragraph text="RESPONSE_ERROR_404" />
                        ) : errStatus ? (
                            <Paragraph text="SLUZBA_NEDOSTUPNA" />
                        ) : null}
                        <div
                            style={{
                                paddingTop: '10px'
                            }}
                        >
                            <ButtonComponent
                                id="errZpetBtn"
                                type="default"
                                buttontext="ZPET"
                                onClick={() => {
                                    // navigateWorkaround(
                                    //     // eslint-disable-next-line max-len
                                    //     `/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_PREHLED_AKTIVNICH_POZADAVKU}`
                                    // );
                                    navigateWorkaround(-1);
                                }}
                            />
                        </div>
                    </Col>
                </Row>
            ) : (
                <>
                    {(specialniSluzby?.detail?.typprevodu || prehledData?.typprevodu) !== ETypPrevodu.BEZZAPLACENI && (
                        <DvpSeznam
                            setRefetchDFP={setRefetch}
                            typprevodu={specialniSluzby?.detail?.typprevodu || prehledData?.typprevodu}
                            setIsDvpDetail={setIsDvpDetail}
                            isAfterMezniDatum={isAfterMezniDatum(true)}
                        />
                    )}
                    {!isDvpDetail && (
                        <>
                            {(specialniSluzby?.detail?.typprevodu || prehledData?.typprevodu) ===
                                ETypPrevodu.PROTIABEZZAPL ||
                            (specialniSluzby?.detail?.typprevodu || prehledData?.typprevodu) ===
                                ETypPrevodu.PXVNABEZZAPL ? (
                                <Collapse
                                    expandIconPosition="left"
                                    bordered={false}
                                    defaultActiveKey="detail"
                                    className="Collapsedivider"
                                    expandIcon={({ isActive }) => (
                                        <Row
                                            style={{
                                                width: '100%'
                                            }}
                                            gutter={10}
                                        >
                                            <Col>
                                                <Title
                                                    level={4}
                                                    text={`${
                                                        getLanguageObject(language)
                                                            .DOKUMENT_DODATEK_CP_SPUSOB_PRIKAZ_BEZZAPLACENI_SHORT
                                                    }`}
                                                />
                                            </Col>
                                            <Col>
                                                <DownOutlined
                                                    rotate={isActive ? 180 : 0}
                                                    style={{
                                                        fontSize: 18,
                                                        top: 10
                                                    }}
                                                />
                                            </Col>
                                            <Col flex={10}>
                                                <Divider className="TitleDivider" />
                                            </Col>
                                        </Row>
                                    )}
                                >
                                    <Panel header={''} key="detail">
                                        {(specialniSluzby?.detail?.typprevodu || prehledData?.typprevodu) ===
                                            ETypPrevodu.PXVNABEZZAPL && (
                                            <RowTitles
                                                firstText="POCET_CP_MAX"
                                                secondText={formatInputNumberThousand(
                                                    specialniSluzbyDVP?.detailDVP?.vnresult
                                                        ? specialniSluzbyDVP?.detailDVP?.celkpoccp -
                                                              specialniSluzbyDVP?.detailDVP?.pocetcp
                                                        : (specialniSluzbyDVP?.detailDVP?.celkpoccp || 0) -
                                                              (specialniSluzbyDVP?.detailDVP?.pocetcpvn || 0)
                                                )}
                                                paragraph={true}
                                                style={rowTitleStyles}
                                                span={rowsSpan}
                                            />
                                        )}
                                        {renderTable()}
                                        {renderButtons()}
                                    </Panel>
                                </Collapse>
                            ) : (specialniSluzby?.detail?.typprevodu || prehledData?.typprevodu) ===
                              ETypPrevodu.BEZZAPLACENI ? (
                                <>
                                    {renderTable()}
                                    {renderButtons()}
                                </>
                            ) : null}

                            {renderSelectedDetail()}
                        </>
                    )}
                    {(!isDvpDetail &&
                        (prehledData?.typprevodu || specialniSluzby?.detail?.typprevodu) ===
                            ETypPrevodu.PROTIABEZZAPL) ||
                        ((prehledData?.typprevodu || specialniSluzby?.detail?.typprevodu) ===
                            ETypPrevodu.PXVNABEZZAPL &&
                            renderProtiabezzapExtraBtns())}
                </>
            )}
            <ErrModal modalMsg={errModalMsg} setModalMsg={setErrModalMsg} />
        </LoadingWrapper>
    );
};

export default DetailPozadavku;
